<script>
/**
 * Drop-in wrapper around <AgGridVue> that adds a Grid/Card view toggle.
 *
 * Usage: replace `<AgGridVue ... />` with `<WsGrid :card-rows="rows" ... />`.
 * All other ag-grid props/listeners pass straight through via $attrs, so the
 * grid behaves exactly as before. The selected view persists in localStorage
 * (shared across every WorkSched grid) and is forced to "card" on mobile.
 *
 * Infinite-row grids depend on their datasource to populate `card-rows`, so in
 * card view we keep them mounted but rendered off-screen — that preserves a
 * real viewport (the infinite model only loads blocks for a sized grid) and
 * keeps filter-driven refreshes flowing. Client-side (rowData) grids get their
 * `card-rows` straight from the page array, so we unmount them in card view.
 */
import DataCardList from "@/components/work-sched/data-card-list.vue";
import { dataView, setDataView } from "@/state/data-view";

export default {
  name: "WsGrid",
  inheritAttrs: false,
  components: { DataCardList },
  props: {
    columnDefs: { type: Array, required: true },
    cardRows: { type: Array, default: () => [] },
    cardTitleField: { type: String, default: "" },
    cardPageSize: { type: Number, default: 12 },
    cardEmptyText: { type: String, default: "No records to display" },
    gridHeight: { type: String, default: "560px" },
    loading: { type: Boolean, default: false },
  },
  emits: ["card-click"],
  data() {
    return { view: dataView };
  },
  computed: {
    effectiveView() {
      return this.view.isMobile ? "card" : this.view.mode;
    },
    isInfinite() {
      return this.$attrs.rowModelType === "infinite";
    },
    // Infinite grids must stay mounted (off-screen) in card view to keep
    // feeding card-rows; client-side grids can unmount.
    renderGrid() {
      return this.effectiveView === "grid" || this.isInfinite;
    },
    gridOffscreen() {
      return this.effectiveView !== "grid";
    },
  },
  watch: {
    effectiveView(v) {
      // Re-flow the grid after it returns on-screen (it laid out at a
      // different width while parked off-screen).
      if (v === "grid") {
        this.$nextTick(() => window.dispatchEvent(new Event("resize")));
      }
    },
  },
  methods: {
    setView(mode) {
      setDataView(mode);
    },
  },
};
</script>

<template>
  <div class="ws-grid">
    <div v-if="!view.isMobile" class="ws-grid__bar">
      <div class="btn-group btn-group-sm ws-grid__toggle" role="group" aria-label="View mode">
        <button
          type="button"
          class="btn"
          :class="view.mode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'"
          title="Grid view"
          @click="setView('grid')"
        >
          <i class="bx bx-table"></i>
        </button>
        <button
          type="button"
          class="btn"
          :class="view.mode === 'card' ? 'btn-primary' : 'btn-outline-secondary'"
          title="Card view"
          @click="setView('card')"
        >
          <i class="bx bx-grid-alt"></i>
        </button>
      </div>
    </div>

    <div
      v-if="renderGrid"
      :class="['ws-grid__gridwrap', { 'ws-grid__gridwrap--offscreen': gridOffscreen }]"
      :aria-hidden="gridOffscreen ? 'true' : null"
    >
      <AgGridVue
        class="ag-theme-alpine"
        :style="{ height: gridHeight, width: '100%' }"
        :columnDefs="columnDefs"
        v-bind="$attrs"
      />
      <!-- Reactive loading overlay for grid view. ag-grid's own overlay depends
           on gridReady having fired, which races with fast fetches; this is
           driven straight off :loading so it always shows. -->
      <div v-if="loading && effectiveView === 'grid'" class="ws-grid__overlay">
        <span class="ws-grid__spinner" role="status" aria-live="polite">
          <span class="visually-hidden">Loading…</span>
        </span>
      </div>
    </div>

    <DataCardList
      v-if="effectiveView === 'card'"
      :column-defs="columnDefs"
      :rows="cardRows"
      :title-field="cardTitleField"
      :page-size="cardPageSize"
      :empty-text="cardEmptyText"
      :loading="loading"
      @row-click="$emit('card-click', $event)"
    />
  </div>
</template>

<style>
.ws-grid {
  position: relative;
}
.ws-grid__bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.6rem;
}
.ws-grid__toggle .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  font-size: 1.05rem;
}
.ws-grid__toggle .btn-primary {
  background-color: #f57c00;
  border-color: #f57c00;
}
.ws-grid__toggle .btn-outline-secondary {
  color: #6c757d;
}
.ws-grid__gridwrap {
  position: relative;
}
.ws-grid__overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.82);
  border-radius: 8px;
}
.ws-grid__spinner {
  width: 2.4rem;
  height: 2.4rem;
  border: 0.25rem solid rgba(245, 124, 0, 0.2);
  border-top-color: #f57c00;
  border-radius: 50%;
  animation: ws-grid-spin 0.7s linear infinite;
}
@keyframes ws-grid-spin {
  to { transform: rotate(360deg); }
}
/* Park an infinite grid off-screen (but fully sized, so its datasource keeps
   loading) while the card list is shown. */
.ws-grid__gridwrap--offscreen {
  position: absolute;
  left: -99999px;
  top: 0;
  width: 1200px;
  pointer-events: none;
}
</style>
